import {
  adjacent,
  cell,
  neighbors,
  option,
  random,
  reject,
  shuffled,
} from './53-game-primitives.mjs';
import { brookPuzzle } from './59-idle-puzzles.mjs';

/** Author a reachable stream route before rocks/checkpoints. Level1 retains the
 * introductory5×5 map; later6×6 maps add3–5 ordered flowers and a straight wooden
 * aqueduct crossing. A bounded DFS falls back to a legal deterministic detour.
 * Witnesses never enter game state or renderer metadata. */
export function brookChallengePuzzle(state, stage = 1) {
  if (!Number.isInteger(stage) || stage < 1 || stage > 99) reject('Invalid brook level.');
  if (stage === 1) return { ...brookPuzzle(state), columns: 5, bridge: null };
  const columns = 6,
    end = 35,
    route = [0];
  let budget = 4500;
  const minimum = Math.min(26, 16 + stage * 2);
  function walk(at) {
    if (--budget < 0) return false;
    if (at === end) return route.length >= minimum;
    if (route.length >= 31) return false;
    for (const next of shuffled(state, neighbors(at, columns, 36))) {
      if (route.includes(next)) continue;
      route.push(next);
      if (walk(next)) return true;
      route.pop();
    }
    return false;
  }
  if (!walk(0))
    route.splice(
      0,
      route.length,
      0,
      1,
      2,
      3,
      4,
      5,
      11,
      10,
      9,
      8,
      7,
      6,
      12,
      13,
      14,
      15,
      16,
      17,
      23,
      22,
      21,
      20,
      19,
      18,
      24,
      25,
      26,
      27,
      28,
      29,
      35,
    );
  const straight = route.slice(2, -2).filter((index) => {
    const n = route.indexOf(index);
    return route[n] - route[n - 1] === route[n + 1] - route[n];
  });
  const bridgeIndex = straight[Math.floor(straight.length / 2)];
  const position = route.indexOf(bridgeIndex);
  const bridge =
    bridgeIndex === undefined
      ? null
      : {
          index: bridgeIndex,
          axis: Math.abs(route[position - 1] - bridgeIndex) === 1 ? 'horizontal' : 'vertical',
        };
  const count = Math.min(5, 2 + Math.floor((stage + 1) / 2));
  const candidates = route.slice(1, -1).filter((index) => index !== bridgeIndex);
  const checkpoints = Array.from(
    { length: count },
    (_, i) => candidates[Math.floor(((i + 1) * candidates.length) / (count + 1))],
  );
  const blocked = Array.from({ length: 36 }, (_, i) => i).filter(
    (i) => !route.includes(i) && random(state, 4) !== 0,
  );
  return { columns, blocked, checkpoints, bridge, solution: [...route] };
}

/** Initialize an unscored private level. Canonical path begins at the spring;
 * changing a level is explicit and never happens merely because time passed. */
export function createBrookChallenge(state, stage = 1) {
  const { columns, blocked, checkpoints, bridge } = brookChallengePuzzle(state, stage);
  state.data = {
    columns,
    blocked,
    checkpoints,
    bridge,
    path: [0],
    stage,
    stageComplete: false,
    events: [],
  };
}

function bridgeAllows(data, from, to) {
  if (!data.bridge || ![from, to].includes(data.bridge.index)) return true;
  return Math.abs(from - to) === (data.bridge.axis === 'horizontal' ? 1 : data.columns);
}
function available(data, index) {
  const back = index === data.path.at(-2),
    current = data.path.at(-1);
  if (back) return true;
  if (
    data.path.includes(index) ||
    data.blocked.includes(index) ||
    !adjacent(current, index, data.columns) ||
    !bridgeAllows(data, current, index)
  )
    return false;
  const missing = data.checkpoints.filter((i) => !data.path.includes(i));
  if (data.checkpoints.includes(index) && missing[0] !== index) return false;
  const crossed = !data.bridge || data.path.includes(data.bridge.index);
  return index !== data.columns ** 2 - 1 || (!missing.length && crossed);
}

/** Apply one connected route segment, backtrack, same-map reset or explicit
 * Next. Flowers must be watered in order and aqueduct entry/exit follows its
 * axis. Rejected actions preserve path and RNG; completed levels remain held. */
export function moveBrookChallenge(state, move) {
  const d = state.data;
  if (move.type === 'option' && move.index === 2 && d.stageComplete) {
    createBrookChallenge(state, Math.min(99, d.stage + 1));
    state.moves = 0;
    return;
  }
  if (d.stageComplete) reject('This brook is complete. Choose Next level.');
  d.events = [];
  if (move.type === 'option') {
    if (move.index === 0 && d.path.length > 1) d.path.pop();
    else if (move.index === 1) d.path = [0];
    else reject('That brook tool is unavailable.');
    return;
  }
  if (
    move.type !== 'cell' ||
    !Number.isInteger(move.index) ||
    move.index < 0 ||
    move.index >= d.columns ** 2 ||
    !available(d, move.index)
  )
    reject('Connect open ground, visit flowers in order and follow the aqueduct.');
  if (move.index === d.path.at(-2)) d.path.pop();
  else {
    d.path.push(move.index);
    if (d.checkpoints.includes(move.index))
      d.events = [{ type: 'bloom', index: move.index, points: 0 }];
  }
  d.stageComplete = d.path.at(-1) === d.columns ** 2 - 1;
  if (d.stageComplete) d.events = [{ type: 'complete', reason: 'level', points: 0 }];
}

/** Public route/terrain projection; no solver witness or future answer is
 * included. Six columns retain44px targets at the supported320px viewport. */
export function brookChallengeView(state) {
  const d = state.data,
    end = d.columns ** 2 - 1,
    missing = d.checkpoints.filter((i) => !d.path.includes(i));
  const crossed = !d.bridge || d.path.includes(d.bridge.index);
  const cells = Array.from({ length: d.columns ** 2 }, (_, index) => {
    const checkpoint = d.checkpoints.indexOf(index) + 1,
      blocked = d.blocked.includes(index),
      watered = d.path.includes(index);
    return cell(
      index,
      `${index === 0 ? 'Spring' : index === end ? 'Pool' : checkpoint ? `Flower ${checkpoint}${watered ? ', watered' : ''}` : d.bridge?.index === index ? `${d.bridge.axis} aqueduct${watered ? ', crossed' : ''}` : blocked ? 'Rock' : 'Open bank'} · row ${Math.floor(index / d.columns) + 1}, column ${(index % d.columns) + 1}`,
      {
        value: watered ? 1 : 0,
        blocked,
        checkpoint,
        selected: watered,
        destination: !watered && available(d, index),
        disabled: d.stageComplete || !available(d, index),
        bridge: d.bridge?.index === index ? d.bridge.axis : null,
        colour: watered ? 4 : undefined,
      },
    );
  });
  return {
    columns: d.columns,
    cells,
    options: [
      { ...option(0, 'Step back'), disabled: d.stageComplete || d.path.length <= 1 },
      { ...option(1, 'Reset stream'), disabled: d.stageComplete || d.path.length <= 1 },
      {
        ...option(2, d.stage === 99 ? 'Another expert puzzle' : 'Next level'),
        disabled: !d.stageComplete,
      },
    ],
    advanceLabel: d.stage === 99 ? 'Another expert puzzle' : 'Next level',
    completionTitle: d.stage === 99 ? 'Expert valley restored!' : null,
    family: 'brook',
    stage: d.stage,
    stageComplete: d.stageComplete,
    advanceAction: d.stageComplete ? { type: 'option', index: 2 } : null,
    goal: `Water ${d.checkpoints.length} flowers in order${d.bridge ? ' and cross the wooden aqueduct' : ''}, then reach the pool.`,
    status: d.stageComplete
      ? d.stage === 99
        ? 'Expert valley restored!'
        : `Valley ${d.stage} restored!`
      : missing.length
        ? `Follow the water · next flower ${d.checkpoints.indexOf(missing[0]) + 1}`
        : !crossed
          ? 'Flowers watered · cross the wooden aqueduct'
          : 'The valley is blooming · bring the stream to the pool',
    progress: {
      current: d.checkpoints.length - missing.length,
      total: d.checkpoints.length,
      label: `${d.checkpoints.length - missing.length} of ${d.checkpoints.length} flowers blooming`,
    },
    scene: {
      path: [...d.path],
      blocked: [...d.blocked],
      checkpoints: [...d.checkpoints],
      columns: d.columns,
      bridge: d.bridge ? { ...d.bridge } : null,
    },
  };
}
